import { Link } from "react-router-dom";
import { Shield, Check, Phone, Star, MapPin, AlertTriangle, Clock } from "lucide-react";
import { Logo } from "../components/Layout";
import { Header } from "../components/Layout";
import IpswichBreadcrumb from "../components/IpswichBreadcrumb";

const suburbs = ["Ipswich CBD", "Brassall", "Booval", "Bundamba", "Goodna", "Redbank Plains", "Collingwood Park", "Bellbird Park", "Ripley", "Springfield", "North Ipswich", "East Ipswich", "Leichhardt", "Silkstone", "Sadliers Crossing"];

export default function TermiteTreatmentIpswichPage() {
  return (
    <>
      <>
        <title>Termite Treatment Ipswich | Barriers & Baiting | Pest Free</title>
        <meta name="description" content="Professional termite treatment in Ipswich QLD. Chemical barriers, baiting systems & active termite elimination. QBCC licensed, fully insured. Protect your biggest investment." />
        <link rel="canonical" href="https://pestfreebrisbane.com.au/termite-treatment-ipswich" />
      </>

      <Header />
      <div className="min-h-screen bg-slate-50">
        <IpswichBreadcrumb current="Termite Treatment" />
        <section className="bg-gradient-to-br from-[#3E2723] via-[#4E342E] to-[#D2691E]/20 text-white pt-28 pb-16 md:pt-32 md:pb-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#D2691E]/20 border border-[#D2691E]/30 rounded-full px-4 py-1.5 mb-5">
                  <MapPin className="w-4 h-4 text-[#D2691E]" />
                  <span className="text-sm font-medium text-[#D2691E]">Ipswich QLD</span>
                </div>
                <h1 className="text-4xl md:text-5xl font-black mb-4 leading-tight" data-testid="termite-treatment-h1">
                  Termite Treatment <span className="text-[#D2691E]">Ipswich</span>
                </h1>
                <p className="text-lg text-slate-300 mb-6">
                  Ipswich sits in one of Australia's highest termite-risk zones. Whether you've found active termites or want to protect your home with a chemical barrier, our licensed technicians deliver proven treatment solutions that last.
                </p>
                <div className="flex flex-wrap gap-3 mb-6">
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#D2691E]" />QBCC Licensed</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#D2691E]" />Fully Insured</div>
                  <div className="flex items-center gap-1.5 text-sm text-slate-300"><Check className="w-4 h-4 text-[#D2691E]" />Warranty Backed</div>
                </div>
                <div className="flex items-center gap-3">
                  <a href="tel:0737081422" className="flex items-center gap-2 bg-[#D2691E] hover:bg-[#b8571a] text-white font-semibold px-6 py-3 rounded-full transition-all" data-testid="termite-treatment-call-btn">
                    <Phone className="w-5 h-5" /> (07) 3708 1422
                  </a>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />)}
                    <span className="text-sm text-slate-300 ml-1">4.9 on Google</span>
                  </div>
                </div>
              </div>
              <div className="flex justify-center">
                <Logo className="h-32 md:h-40 opacity-90" />
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-800 text-center mb-3">Termite Treatment Options for Ipswich Homes</h2>
            <p className="text-slate-600 text-center mb-10 max-w-3xl mx-auto">Every Ipswich property is different. We assess your home's construction, soil type and risk level to recommend the most effective termite treatment.</p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                <div className="w-12 h-12 rounded-full bg-[#D2691E]/10 flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-[#D2691E]" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-3">Chemical Termite Barrier</h3>
                <p className="text-sm text-slate-600 mb-4">A continuous chemical barrier is applied around the perimeter and sub-floor of your home. This creates a treated zone that eliminates termites on contact and repels future colonies.</p>
                <ul className="space-y-2">
                  {["Trenching around external perimeter", "Drilling through concrete paths & slabs", "Sub-floor treatment where accessible", "5 to 8 year protection depending on product", "Compliant with AS 3660.2 standards"].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-[#D2691E] flex-shrink-0 mt-0.5" />{item}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                <div className="w-12 h-12 rounded-full bg-[#7ABC32]/10 flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-[#7ABC32]" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-3">Termite Baiting System</h3>
                <p className="text-sm text-slate-600 mb-4">In-ground bait stations are installed around your property and monitored regularly. Termites feed on the bait and share it with the colony, eliminating the entire nest over time.</p>
                <ul className="space-y-2">
                  {["Bait stations installed around perimeter", "Non-invasive — no drilling or trenching", "Colony elimination (not just deterrence)", "Ongoing monitoring and bait replenishment", "Ideal for slab-on-ground construction"].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-[#7ABC32] flex-shrink-0 mt-0.5" />{item}</li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="text-center mt-8">
              <a href="tel:0737081422" className="inline-flex items-center gap-2 bg-[#D2691E] hover:bg-[#b8571a] text-white font-bold px-8 py-3 rounded-full transition-all">
                <Phone className="w-5 h-5" /> Get a Free Termite Treatment Quote
              </a>
            </div>
          </div>
        </section>

        <section className="py-12 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">Why Ipswich Homes Are High Risk for Termites</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                { title: "Clay & Loam Soil Types", desc: "Much of Ipswich sits on clay and alluvial soil that retains moisture — the perfect environment for subterranean termites to build mud tubes and travel undetected.", color: "#D2691E" },
                { title: "Older Timber Construction", desc: "Heritage and post-war homes in suburbs like Booval, Brassall and North Ipswich have extensive hardwood framing that termites actively target.", color: "#D2691E" },
                { title: "New Developments on Former Farmland", desc: "Ripley Valley and Springfield developments disturb established termite colonies, forcing them to seek new food sources in nearby homes.", color: "#E74C3C" },
                { title: "Humid Subtropical Climate", desc: "Ipswich's warm, wet summers accelerate termite colony growth. A single colony can consume a linear metre of structural timber in as little as 3 months.", color: "#E74C3C" },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-5 rounded-xl bg-white border border-slate-200">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: item.color + '15' }}>
                    <AlertTriangle className="w-5 h-5" style={{ color: item.color }} />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800">{item.title}</h3>
                    <p className="text-sm text-slate-600 mt-1">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl font-bold text-slate-800 text-center mb-8">Termite Treatment FAQ — Ipswich</h2>
            <div className="space-y-4">
              {[
                { q: "How much does termite treatment cost in Ipswich?", a: "Termite treatment costs vary based on property size, construction type and treatment method. Chemical barriers typically range from $2,500 to $5,000+ for an average Ipswich home. Baiting systems have a lower upfront cost with ongoing monitoring fees. Call us for a free on-site quote." },
                { q: "What's the difference between termite treatment and termite inspection?", a: "A termite inspection identifies whether termites are present and assesses risk. Termite treatment is the active elimination and prevention step — either a chemical barrier or baiting system installed to protect your home. We recommend annual inspections and treatment if activity is found." },
                { q: "How long does a chemical termite barrier last?", a: "Modern chemical barriers using products like Termidor or Altriset last 5 to 8 years depending on soil conditions, drainage and product used. Ipswich's clay soils generally help barriers last longer as the chemical binds well to the soil." },
                { q: "Can you treat termites in a slab-on-ground home?", a: "Yes. We drill through concrete slabs at intervals to inject termiticide beneath the slab and around the perimeter. This is a common method in Ipswich's newer estates like Ripley and Springfield where slab construction is standard." },
                { q: "Should I choose a chemical barrier or baiting system?", a: "It depends on your property. Chemical barriers provide immediate, long-lasting protection and are ideal for most Ipswich homes. Baiting systems are better for properties where drilling is difficult or where a non-invasive approach is preferred. We'll recommend the best option after inspecting your property." },
              ].map((item, i) => (
                <div key={i} className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                  <h3 className="font-bold text-slate-800 mb-2">{item.q}</h3>
                  <p className="text-sm text-slate-600">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">Termite Treatment Across Ipswich</h2>
            <div className="flex flex-wrap justify-center gap-2">
              {suburbs.map(s => (
                <span key={s} className="bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-medium text-slate-700">{s}</span>
              ))}
            </div>
            <p className="text-slate-500 text-sm mt-4">And all surrounding areas in the Ipswich region</p>
          </div>
        </section>

        <section className="py-8 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-lg font-bold text-slate-800 text-center mb-4">Related Services in Ipswich</h2>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/termite-inspection-ipswich" className="text-sm text-[#D2691E] hover:underline font-medium">Termite Inspection Ipswich</Link>
              <span className="text-slate-300">|</span>
              <Link to="/pest-control-ipswich" className="text-sm text-[#E74C3C] hover:underline font-medium">Pest Control Ipswich</Link>
              <span className="text-slate-300">|</span>
              <Link to="/cockroach-treatment-ipswich" className="text-sm text-[#E74C3C] hover:underline font-medium">Cockroach Treatment Ipswich</Link>
              <span className="text-slate-300">|</span>
              <Link to="/bird-control-ipswich" className="text-sm text-[#5BC0DE] hover:underline font-medium">Bird Control Ipswich</Link>
              <span className="text-slate-300">|</span>
              <Link to="/end-of-lease-pest-control-ipswich" className="text-sm text-[#7ABC32] hover:underline font-medium">End of Lease Pest Control</Link>
            </div>
          </div>
        </section>

        <section className="py-12 bg-gradient-to-r from-[#3E2723] to-[#D2691E] text-white text-center">
          <div className="max-w-3xl mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Protect Your Ipswich Home from Termites</h2>
            <p className="text-lg text-white/80 mb-6">Don't wait until the damage is done. Get a professional termite treatment quote today.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="tel:0737081422" className="inline-flex items-center gap-2 bg-white text-[#D2691E] font-bold px-8 py-3 rounded-full hover:bg-slate-100 transition-all">
                <Phone className="w-5 h-5" /> Call (07) 3708 1422
              </a>
              <Link to="/termite-inspection-ipswich" className="inline-flex items-center gap-2 border-2 border-white text-white font-bold px-8 py-3 rounded-full hover:bg-white/10 transition-all">
                Book Inspection First
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
